// frontend/src/lib/supabaseAuth.ts
//
// Lazy singleton Supabase client scoped to the passkey sign-in flow.
//
// `persistSession: false` is deliberate: this client's session is only a
// stepping stone between a WebAuthn ceremony and either (a) our ops JWT via
// POST /api/auth/passkey/exchange, or (b) a passkey registration/management
// call. We never want a Supabase session lingering in localStorage alongside
// the ops JWT that AuthContext already owns.
import { createClient, SupabaseClient } from '@supabase/supabase-js';

let client: SupabaseClient | null | undefined;

/**
 * Returns the shared Supabase client, or null when passkey auth is not
 * configured in this environment (missing URL/publishable key). Callers must
 * treat a null return as "hide passkey UI", not as an error.
 */
export function getSupabaseAuthClient(): SupabaseClient | null {
  if (client !== undefined) {
    return client;
  }

  const url = process.env.REACT_APP_SUPABASE_URL;
  const publishableKey = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

  if (!url || !publishableKey) {
    client = null;
    return client;
  }

  // The passkey surface (signInWithPasskey, registerPasskey, auth.passkey.*)
  // is EXPERIMENTAL in supabase-js and may change between minor versions —
  // the pinned version in package.json is load-bearing; re-verify these
  // calls when bumping it.
  client = createClient(url, publishableKey, {
    auth: {
      experimental: { passkey: true },
      persistSession: false,
    },
  });

  return client;
}
